import { useRequiredContext } from "@redotech/react-util/context";
import { useSupportHelpdeskV2Enabled } from "@redotech/redo-merchant-app-common/setting/use-support-helpdesk-v2-enabled";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { ACTIVE_CONVERSATION_QUERY_PARAMETER } from "@redotech/redo-merchant-support-app/query-parameters";
import { memo, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * Redirects support-v1 routes to their support-v2 equivalents when the team
 * has support-v2 enabled. Wraps support-v1 page elements so users on v2 are
 * sent to the v2 inbox or detail view instead of the legacy experience.
 *
 * `/support/table/...` → `/support-v2`
 * `/support/table/...?activeConversationId=X` → `/support-v2/X`
 */
export const SupportV2RedirectGuard = memo(function SupportV2RedirectGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const { pending, enabled } = useSupportHelpdeskV2Enabled();
  const location = useLocation();

  if (pending) {
    return null;
  }

  if (!enabled) {
    return <>{children}</>;
  }

  const supportV2BaseUrl = `/stores/${String(team._id)}/support-v2`;

  const conversationId =
    new URLSearchParams(location.search).get(
      ACTIVE_CONVERSATION_QUERY_PARAMETER,
    ) || undefined;

  const target = conversationId
    ? `${supportV2BaseUrl}/${conversationId}`
    : supportV2BaseUrl;

  return <Navigate replace to={target} />;
});
